import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Link, IconButton, Snackbar, Slide } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { SlideProps } from '@mui/material/Slide';

import { AuthRole } from '@libs/types';
import { useNotification } from '@carcareers/contexts';
import { useAuth } from '@carcareers/contexts';
import { routes } from '@carcareers/utils';

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

export const CompleteProfileNotification = () => {
  const { user } = useAuth();
  const { isProfileComplete, hasCheckedProfile } = useNotification();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (user?.role === AuthRole.JOBSEEKER && hasCheckedProfile && isProfileComplete === false && !isDismissed) {
      setOpen(true);
    }
  }, [user?.role, hasCheckedProfile, isProfileComplete, isDismissed]);

  const handleClose = () => {
    setOpen(false);
    setIsDismissed(true);
  };

  const handleCompleteProfile = () => {
    navigate(`${routes.JOBSEEKER_HOME}`);
    setOpen(false);
    setIsDismissed(true);
  };

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      autoHideDuration={null}
      TransitionComponent={SlideTransition}
      sx={{
        '& .MuiSnackbarContent-root': {
          minWidth: { xs: '320px', sm: '400px' },
        },
      }}
    >
      <Alert
        severity="info"
        sx={{
          width: { xs: '320px', sm: '400px' },
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderRadius: 4,
          borderColor: 'divider',
          color: 'text.primary',
          '& .MuiAlert-icon': {
            color: 'primary.main',
            pt: '10px',
          },
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
            <AlertTitle sx={{ margin: 0, color: 'inherit', fontSize: '1.1rem', fontWeight: 600 }}>
              Complete Your Profile
            </AlertTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              <Link
                component="button"
                variant="body2"
                onClick={handleCompleteProfile}
                sx={{
                  textDecoration: 'underline',
                  color: 'inherit',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  '&:hover': {
                    textDecoration: 'none',
                  },
                }}
              >
                Profile
              </Link>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  color: 'inherit',
                  ml: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ fontSize: '0.875rem', lineHeight: 1.4, opacity: 0.95 }}>
            Stand out to employers with a complete profile. Showcase your experience, education, and skills to unlock
            better job opportunities and increase your visibility to hiring managers.
          </Box>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default CompleteProfileNotification;
